import { ColumnType } from '@dynamika-shared/ui/Table/Table.types';
import { SiteSubjectType } from '@restore/ExternalUserSubjects/ExternalUserSubjects.types';
import { format, parse } from 'date-fns';
import { TruncatedText } from '@dynamika-shared/ui/TruncatedText/TruncatedText';

export function makeExternalUserSubjectListColumns(
  sites?: Array<{ id: string; name: string; code?: string }>
): ColumnType<SiteSubjectType>[] {
  return [
    {
      name: 'code',
      tdClassName: 'w-[210px]',
      header() {
        return 'Subject ID';
      },
      loading: () => (
        <div className='h-2 w-10 animate-pulse rounded-md bg-slate-300'></div>
      ),
      getValue(row) {
        return <TruncatedText text={row.patientCode || row.code} truncate tooltip />;
      },
    },
    {
      name: 'birthDate',
      tdClassName: 'w-[190px]',
      header() {
        return 'Date of Birth';
      },
      loading: () => (
        <div className='h-2 w-10 animate-pulse rounded-md bg-slate-300'></div>
      ),
      getValue(row) {
        const formattedDate = parse(
          row.dob.replace('.', '-'),
          'MM-yyyy',
          new Date()
        );
        return format(formattedDate, 'MMM-yyyy');
      },
    },
    {
      name: 'site',
      tdClassName: 'w-[210px]',
      header() {
        return 'Site';
      },
      loading: () => (
        <div className='h-2 w-10 animate-pulse rounded-md bg-slate-300'></div>
      ),
      getValue(row) {
        const site = sites?.find(s => s.id === row.siteId);
        return <div className=''>{site?.code || site?.name || '-'}</div>;
      },
    },
    {
      name: 'cohort',
      tdClassName: 'w-[210px]',
      header() {
        return 'Cohort';
      },
      loading: () => (
        <div className='h-2 w-10 animate-pulse rounded-md bg-slate-300'></div>
      ),
      getValue(row) {
        return <div className=''>{row.cohortName || '-'}</div>;
      },
    },
    {
      name: 'lastUploadedVisitName',
      hasSorting: true,
      header() {
        return <p className='flex items-center'>Last Uploaded Visit</p>;
      },
      loading: () => (
        <div className='h-2 w-10 animate-pulse rounded-md bg-slate-300'></div>
      ),
      getValue(row) {
        const lastVisit = row.lastVisit;
        const isUnscheduled =
          lastVisit?.type === 'UNSCHEDULED' && !lastVisit?.earlyTermination;
        const visitName = isUnscheduled ? (
          <div>
            <div>Unscheduled</div>
            <div>
              {format(
                new Date((lastVisit?.latestUpload || lastVisit?.name) ?? '-'),
                'dd-MMM-yyy'
              )}
            </div>
          </div>
        ) : (
          row.lastUploadedVisitName || lastVisit?.name || '-'
        );
        return <div className=''>{visitName}</div>;
      },
    },
    {
      name: 'lastUploadedDate',
      hasSorting: true,
      tdClassName: 'w-[190px]',
      header() {
        return <p className='flex items-center'>Last Uploaded Date</p>;
      },
      loading: () => (
        <div className='h-2 w-10 animate-pulse rounded-md bg-slate-300'></div>
      ),
      getValue(row) {
        const lastVisit = row.lastVisit;
        const uploadDate = lastVisit?.latestUpload || row.lastUploadedDate;

        if (!uploadDate) {
          return <div className=''>-</div>;
        }

        try {
          const date = new Date(uploadDate);
          return <div className=''>{format(date, 'dd-MMM-yyyy')}</div>;
        } catch (error) {
          console.error('Error formatting date:', uploadDate, error);
          return <div className=''>-</div>;
        }
      },
    },
  ];
}
